import { Link } from 'react-router-dom'
import { motion, useScroll, useTransform } from 'framer-motion'
import { Share2, Megaphone, GitBranch, ArrowRight } from 'lucide-react'
import { useRef } from 'react'

const items = [
  {
    title: 'Brand Presence',
    subtitle: 'Before leads come trust.',
    body: 'We help businesses build a strong and credible digital presence through strategic content and brand communication. The goal is simple: When your audience sees your brand, they recognize authority and credibility.',
    href: '/services/social-media-marketing',
    linkText: 'Social media marketing & brand presence',
    icon: Share2,
  },
  {
    title: 'Lead Generation (Meta Ads)',
    subtitle: 'Our core expertise.',
    body: 'We design targeted advertising campaigns that attract high-intent prospects who are ready to engage with your business. The objective is not traffic. The objective is qualified business opportunities.',
    href: '/services/paid-ads',
    linkText: 'Meta Ads & paid lead generation',
    icon: Megaphone,
  },
  {
    title: 'Sales Process Support',
    subtitle: 'Leads alone do not build businesses.',
    body: 'We help clients design and refine their sales processes so enquiries convert into real customers. Because predictable growth happens when marketing and sales work together.',
    href: '/services/funnel-building',
    linkText: 'Funnel building & sales process',
    icon: GitBranch,
  },
]

export function WhatWeDo() {
  const containerRef = useRef<HTMLDivElement>(null)
  
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ['start end', 'end start']
  })
  
  const y = useTransform(scrollYProgress, [0, 1], [-50, 50])
  const rotate = useTransform(scrollYProgress, [0, 1], [0, 15])

  return (
    <section ref={containerRef} className="section-pad px-4 sm:px-6 lg:px-8 bg-slate-50/80 dark:bg-slate-900/40 relative overflow-hidden">
      <motion.div 
        style={{ y, rotate }}
        className="absolute -left-32 top-1/4 w-80 h-80 lg:w-[500px] lg:h-[500px] pointer-events-none hidden md:block"
      >
        <div className="absolute inset-0 grid grid-cols-3 grid-rows-3 gap-8 p-8 opacity-20 dark:opacity-30">
          {[...Array(9)].map((_, i) => (
            <div key={i} className="relative flex items-center justify-center">
              <div className="w-3 h-3 rounded-full bg-primary dark:bg-accent animate-pulse" style={{ animationDelay: `${i * 0.2}s` }} />
              {i % 2 === 0 && <div className="absolute w-full h-[1px] bg-gradient-to-r from-primary/50 to-transparent rotate-45" />}
              {i % 3 === 0 && <div className="absolute h-full w-[1px] bg-gradient-to-b from-accent/50 to-transparent -rotate-45" />}
            </div>
          ))}
        </div>
      </motion.div>

      <div className="container-wide relative z-10">
        <motion.h2
          className="section-heading text-center mb-3"
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          What We Do
        </motion.h2>
        <motion.p
          className="section-sub text-center max-w-xl mx-auto mb-10 sm:mb-14"
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.05, duration: 0.5 }}
        >
          Services Built For Business Growth
        </motion.p>
        <div className="grid md:grid-cols-3 gap-6 sm:gap-8">
          {items.map((block, i) => (
            <motion.div
              key={block.title}
              className="group relative card-glass rounded-card p-6 sm:p-8 overflow-hidden"
              initial={{ opacity: 0, y: 28 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.08 + i * 0.08, duration: 0.5 }}
              whileHover={{ y: -8, transition: { duration: 0.25 } }}
            >
              <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-accent/5 dark:from-primary/10 dark:to-accent/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              <div className="relative">
                <motion.div
                  className="w-12 h-12 rounded-2xl bg-primary/10 dark:bg-accent/10 flex items-center justify-center mb-4"
                  whileHover={{ scale: 1.08, rotate: 3 }}
                  transition={{ type: 'spring', stiffness: 400 }}
                >
                  <block.icon className="text-primary dark:text-accent" size={24} strokeWidth={1.8} />
                </motion.div>
                <h3 className="mt-4 sm:mt-5 text-lg sm:text-xl font-semibold text-slate-800 dark:text-slate-100">{block.title}</h3>
                <p className="mt-2 text-accent font-medium text-sm">{block.subtitle}</p>
                <p className="mt-4 text-slate-600 dark:text-slate-400 text-sm sm:text-base leading-relaxed">{block.body}</p>
                <Link
                  to={block.href}
                  className="mt-5 inline-flex items-center gap-2 text-primary dark:text-accent font-medium text-sm group/link"
                >
                  {block.linkText}
                  <motion.span
                    className="inline-block"
                    initial={false}
                    whileHover={{ x: 4 }}
                    transition={{ type: 'spring', stiffness: 400 }}
                  >
                    <ArrowRight size={16} strokeWidth={2} />
                  </motion.span>
                </Link>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  )
}
